// src/components/global-gallery.jsx

import React, { useState, useEffect } from 'react';
import {
  collection, query, onSnapshot, doc, getDoc,
  updateDoc, arrayUnion, arrayRemove, addDoc, serverTimestamp, limit, orderBy,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import StyleTryOn from './StyleTryon';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionPage from './SubscriptionPage';
import toast from 'react-hot-toast';

const GOLD = '#B8860B';
const NAVY = '#001F3F';
const SUPER_ADMINS = ['FFXDokpj5IXR3SFSvJyDu70LRp73', 'GbjKMdeVEmTgMefcU1cEHsEOstn1'];

export default function GlobalGallery() {
  const [styles, setStyles] = useState([]);
  const [filteredStyles, setFilteredStyles] = useState([]);
  const [userFaceShape, setUserFaceShape] = useState(null);
  const [userHasScan, setUserHasScan] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  const [activeTryOnUrl, setActiveTryOnUrl] = useState(null);
  const [activeTryOnName, setActiveTryOnName] = useState('');
  const [showSubModal, setShowSubModal] = useState(false);
  const [paywallReason, setPaywallReason] = useState('style_exhausted');

  const navigate = useNavigate();
  const sub = useSubscription();
  const isSuperAdmin = SUPER_ADMINS.includes(auth.currentUser?.uid);

  // Fetch user data + gallery styles
  useEffect(() => {
    if (auth.currentUser) {
      getDoc(doc(db, 'users', auth.currentUser.uid)).then(snap => {
        if (snap.exists()) {
          const data = snap.data();
          setUserFaceShape(data?.faceShape || null);
          setUserHasScan(!!(data?.lastScanUrl || data?.initialRenderUrl || data?.headScan));
        }
      }).catch(() => {});
    }

    const q = query(
      collection(db, 'global_gallery'),
      orderBy('createdAt', 'desc'),
      limit(60)
    );
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ 
        id: d.id, 
        ...d.data() 
      }));
      setStyles(data);
      setFilteredStyles(data);
      setLoading(false);
    }, (err) => {
      console.error('Gallery vault error:', err);
      setLoading(false);
    });

    return () => unsub();
  }, []);

  // Filtering logic
  useEffect(() => {
    let result = styles;

    if (activeFilter !== 'all') {
      result = result.filter(s => 
        s.category?.toLowerCase() === activeFilter.toLowerCase() ||
        s.faceShapes?.some(fs => fs.toLowerCase() === activeFilter.toLowerCase())
      );
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(s => 
        s.name?.toLowerCase().includes(term) || 
        s.creatorName?.toLowerCase().includes(term) ||
        s.businessName?.toLowerCase().includes(term)
      );
    }

    setFilteredStyles(result);
  }, [searchTerm, activeFilter, styles]);

  const handleAITryOn = (imageUrl, styleName) => {
    if (!auth.currentUser) {
      toast.error('Please login to try on styles.');
      navigate('/login');
      return;
    }
    if (!isSuperAdmin && !sub.canStyleTryOn.allowed) {
      setPaywallReason(sub.canStyleTryOn.reason || 'style_exhausted');
      setShowSubModal(true);
      return;
    }
    setActiveTryOnName(styleName || '');
    setActiveTryOnUrl(imageUrl);
  };

  const handleSaveToLookbook = async (style) => {
    if (!auth.currentUser) {
      toast.error('Please login to save styles.');
      navigate('/login');
      return;
    }
    try {
      await addDoc(collection(db, 'users', auth.currentUser.uid, 'customStyles'), {
        sourceStyleId: style.id,
        name: style.name,
        image: style.imageUrl || style.image,
        creatorName: style.creatorName || 'Verified Barber',
        savedAt: serverTimestamp(),
        type: 'lookbook_entry',
      });
      toast.success('Saved to your Personal Library!');
    } catch {
      toast.error('Could not save style.');
    }
  };

  const handleVote = async (styleId, currentVoters = []) => {
    if (!auth.currentUser) {
      toast.error('Login to vote.');
      return;
    }
    const userId = auth.currentUser.uid;
    const isVoted = currentVoters.includes(userId);

    try {
      await updateDoc(doc(db, 'global_gallery', styleId), {
        voters: isVoted ? arrayRemove(userId) : arrayUnion(userId),
        votesCount: isVoted ? (currentVoters.length - 1) : (currentVoters.length + 1),
      });
    } catch (err) {
      console.error(err);
    }
  };

  const freeTriesUsed = sub.tryOnCount ?? 0;

  if (loading) {
    return (
      <div style={loaderContainer}>
        <div style={loadSpinner} />
        <p style={{ color: GOLD, letterSpacing: '4px', fontSize: '0.78rem', marginTop: '16px' }}>
          SCANNING VAULT...
        </p>
      </div>
    );
  }

  return (
    <div style={container}>
      {showSubModal && (
        <SubscriptionPage 
          paywallMode={true} 
          paywallReason={paywallReason}
          onClose={() => setShowSubModal(false)} 
          redirectAfter="/gallery" 
        />
      )}

      {activeTryOnUrl && (
        <StyleTryOn
          initialStyleUrl={activeTryOnUrl}
          initialStyleName={activeTryOnName}
          onClose={() => {
            setActiveTryOnUrl(null);
            setActiveTryOnName('');
          }}
        />
      )}

      <header style={header}>
        <button onClick={() => navigate('/dashboard')} style={backBtn}>←</button>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <h1 style={headerTitle}>GLOBAL STYLE VAULT</h1>
          <p style={{ color: '#555', fontSize: '0.7rem', letterSpacing: '2px', margin: '0 0 12px' }}>
            ESDRAS BIOMETRIC HAIR ARCHITECTURE
          </p>

          {!sub.loading && (
            <div style={quotaStrip}>
              {isSuperAdmin ? (
                <span style={{ color: '#00ff88', fontWeight: '700', fontSize: '0.7rem' }}>
                  ⚡ SUPER ADMIN · UNLIMITED ACCESS
                </span>
              ) : sub.isSubscriptionActive ? (
                <span style={{ color: GOLD, fontSize: '0.7rem', fontWeight: '700' }}>
                  👑 {sub.tier?.toUpperCase()} MEMBER — Unlimited Try-Ons
                </span>
              ) : (
                <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)' }}>
                  FREE TIER • {freeTriesUsed}/1 try-on used •{' '}
                  <span 
                    onClick={() => { setPaywallReason('style_exhausted'); setShowSubModal(true); }}
                    style={{ color: GOLD, cursor: 'pointer', fontWeight: '700' }}
                  >
                    UPGRADE →
                  </span>
                </span>
              )}
            </div>
          )}

          <div style={searchRow}>
            <input 
              style={searchBar} 
              placeholder="Search styles or stylists..." 
              value={searchTerm} 
              onChange={e => setSearchTerm(e.target.value)} 
            />
            <div style={filterTabs}>
              {['all', 'Hair', 'Beard', 'Colour', 'Braids', 'Locs'].map(f => (
                <button 
                  key={f} 
                  onClick={() => setActiveFilter(f)} 
                  style={f === activeFilter ? activeTab : tab}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <div style={galleryGrid}>
        {filteredStyles.length === 0 ? (
          <div style={emptyState}>
            <p style={{ fontSize: '2.5rem', marginBottom: '12px' }}>🔍</p>
            <p style={{ color: GOLD, fontWeight: '700' }}>No styles found</p>
          </div>
        ) : (
          filteredStyles.map(style => {
            const isMatch = userFaceShape && 
              (style.faceShapes?.includes(userFaceShape) || style.idealFor === userFaceShape);
            const hasVoted = style.voters?.includes(auth.currentUser?.uid);

            return (
              <div key={style.id} style={styleCard}>
                <div style={imageWrapper}>
                  {isMatch && <div style={matchBadge}>AI MATCH</div>}
                  <img 
                    src={style.imageUrl} 
                    style={styleImg} 
                    alt={style.name} 
                    loading="lazy" 
                  />
                  <div style={voteBadge} onClick={() => handleVote(style.id, style.voters || [])}>
                    {hasVoted ? '❤️' : '🤍'} {style.votesCount || 0}
                  </div>
                </div>

                <div style={cardContent}>
                  <h3 style={styleName}>{style.name}</h3>
                  <p style={creatorText}>
                    by {style.creatorName || style.businessName || 'Esdras Stylist'}
                  </p>
                  <p style={{ fontSize: '0.78rem', color: GOLD, margin: '6px 0' }}>
                    {style.category} • {style.duration}
                  </p>

                  <div style={actionRow}>
                    <button 
                      onClick={() => handleAITryOn(style.imageUrl, style.name)} 
                      style={tryOnBtn}
                    >
                      ✨ AI Try-On
                    </button>
                    <button 
                      onClick={() => handleSaveToLookbook(style)} 
                      style={saveBtn}
                    >
                      💾 Save
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ==================== STYLES ==================== */
const container = { minHeight: '100vh', background: NAVY, color: 'white', padding: '0 1rem 4rem', fontFamily: 'Montserrat, sans-serif' };
const loaderContainer = { minHeight: '100vh', background: NAVY, display: 'grid', placeItems: 'center', color: GOLD };
const loadSpinner = { width: '40px', height: '40px', border: '3px solid rgba(184,134,11,0.15)', borderTop: `3px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' };

const header = { padding: '40px 0 20px', maxWidth: '1200px', margin: '0 auto' };
const headerTitle = { color: GOLD, fontSize: '2.2rem', fontWeight: '900', textAlign: 'center', marginBottom: '6px', letterSpacing: '2px' };

const searchRow = { display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center' };
const searchBar = { width: '100%', maxWidth: '600px', padding: '15px 25px', borderRadius: '50px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${GOLD}44`, color: 'white', outline: 'none', fontSize: '0.9rem' };

const filterTabs = { display: 'flex', gap: '10px', overflowX: 'auto', width: '100%', justifyContent: 'center', padding: '10px 0' };
const tab = { padding: '8px 18px', borderRadius: '50px', border: '1px solid rgba(255,255,255,0.1)', background: 'none', color: 'white', fontSize: '0.75rem', cursor: 'pointer', whiteSpace: 'nowrap' };
const activeTab = { ...tab, background: GOLD, color: 'black', fontWeight: 'bold' };

const galleryGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '2.5rem', maxWidth: '1200px', margin: '0 auto' };

const styleCard = { background: 'rgba(255,255,255,0.02)', borderRadius: '30px', overflow: 'hidden', border: '1px solid rgba(184,134,11,0.15)' };
const imageWrapper = { position: 'relative', height: '320px', overflow: 'hidden' };
const styleImg = { width: '100%', height: '100%', objectFit: 'cover' };
const matchBadge = { position: 'absolute', top: '15px', left: '15px', background: GOLD, color: 'black', padding: '6px 12px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: 'bold', zIndex: 5 };
const voteBadge = { position: 'absolute', top: '15px', right: '15px', background: 'rgba(0,0,0,0.6)', padding: '6px 14px', borderRadius: '20px', cursor: 'pointer', zIndex: 5, color: 'white', fontSize: '0.8rem' };

const cardContent = { padding: '20px' };
const styleName = { color: GOLD, margin: '0 0 5px', fontSize: '1.2rem', fontWeight: '700' };
const creatorText = { fontSize: '0.8rem', opacity: 0.5, marginBottom: '12px' };

const actionRow = { display: 'flex', flexDirection: 'column', gap: '10px' };
const tryOnBtn = { padding: '12px', background: 'none', border: `1px solid ${GOLD}`, color: GOLD, borderRadius: '50px', fontSize: '0.78rem', cursor: 'pointer', fontWeight: '600' };
const saveBtn = { padding: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '50px', color: 'white', cursor: 'pointer' };

const backBtn = { 
  position: 'absolute', left: '20px', top: '40px', 
  background: 'none', border: `1px solid ${GOLD}`, color: GOLD, 
  padding: '10px 15px', borderRadius: '12px', cursor: 'pointer', fontSize: '1rem' 
};

const quotaStrip = { 
  textAlign: 'center', 
  marginBottom: '16px', 
  padding: '8px 20px', 
  background: 'rgba(255,255,255,0.04)', 
  borderRadius: '50px', 
  display: 'inline-block', 
  border: '1px solid rgba(184,134,11,0.15)' 
};
